import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, ZoomIn, ZoomOut, Crosshair } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_SUPPORTERS = [
  { display_name: 'Malo Le Guen', stars: 3, id: 'fb-1' },
  { display_name: 'Anna Kerbrat', stars: 2, id: 'fb-2' },
  { display_name: 'Yann Le Dantec', stars: 5, id: 'fb-3' },
  { display_name: 'Claire Even', stars: 1, id: 'fb-4' },
  { display_name: 'Tudal M.', stars: 4, id: 'fb-5' },
  { display_name: 'Rozenn Le Berre', stars: 2, id: 'fb-6' },
  { display_name: 'Erwan K.', stars: 3, id: 'fb-7' },
  { display_name: 'Marina Le Floch', stars: 1, id: 'fb-8' },
  { display_name: 'Gwenn & Lena', stars: 6, id: 'fb-9' },
  { display_name: 'Famille Riou', stars: 2, id: 'fb-10' },
  { display_name: 'Nolwenn B.', stars: 4, id: 'fb-11' },
  { display_name: 'Mael Jouan', stars: 1, id: 'fb-12' },
];

const CONSTELLATIONS = [
  {
    id: 'orion',
    name: 'Orion',
    stars: [
      { key: 'betelgeuse', x: 20, y: 18, mag: 1.5 },
      { key: 'bellatrix', x: 30, y: 20, mag: 1.8 },
      { key: 'alnitak', x: 24, y: 30, mag: 2.2 },
      { key: 'alnilam', x: 27, y: 31, mag: 1.7 },
      { key: 'mintaka', x: 30, y: 32, mag: 2.3 },
      { key: 'saiph', x: 24, y: 42, mag: 2.1 },
      { key: 'rigel', x: 33, y: 43, mag: 1.4 },
    ],
    links: [
      ['betelgeuse', 'bellatrix'],
      ['betelgeuse', 'alnitak'],
      ['bellatrix', 'mintaka'],
      ['alnitak', 'alnilam'],
      ['alnilam', 'mintaka'],
      ['alnitak', 'saiph'],
      ['mintaka', 'rigel'],
      ['saiph', 'rigel'],
    ],
    label: { x: 17, y: 12 },
  },
  {
    id: 'cassiopeia',
    name: 'Cassiopee',
    stars: [
      { key: 'schedar', x: 55, y: 12, mag: 2.2 },
      { key: 'caph', x: 60, y: 16, mag: 2.4 },
      { key: 'gamma', x: 65, y: 12, mag: 2.5 },
      { key: 'ruchbah', x: 70, y: 17, mag: 2.7 },
      { key: 'segin', x: 76, y: 11, mag: 3.0 },
    ],
    links: [
      ['schedar', 'caph'],
      ['caph', 'gamma'],
      ['gamma', 'ruchbah'],
      ['ruchbah', 'segin'],
    ],
    label: { x: 60, y: 7 },
  },
  {
    id: 'ursa_major',
    name: 'Grande Ourse',
    stars: [
      { key: 'dubhe', x: 12, y: 62, mag: 1.8 },
      { key: 'merak', x: 18, y: 67, mag: 2.3 },
      { key: 'phecda', x: 24, y: 65, mag: 2.4 },
      { key: 'megrez', x: 28, y: 59, mag: 3.2 },
      { key: 'alioth', x: 35, y: 56, mag: 1.9 },
      { key: 'mizar', x: 42, y: 59, mag: 2.2 },
      { key: 'alkaid', x: 49, y: 64, mag: 1.9 },
    ],
    links: [
      ['dubhe', 'merak'],
      ['merak', 'phecda'],
      ['phecda', 'megrez'],
      ['megrez', 'dubhe'],
      ['megrez', 'alioth'],
      ['alioth', 'mizar'],
      ['mizar', 'alkaid'],
    ],
    label: { x: 10, y: 74 },
  },
  {
    id: 'cygnus',
    name: 'Cygne',
    stars: [
      { key: 'deneb', x: 62, y: 40, mag: 1.3 },
      { key: 'sadr', x: 66, y: 48, mag: 2.3 },
      { key: 'gienah', x: 59, y: 54, mag: 2.5 },
      { key: 'delta', x: 72, y: 55, mag: 2.9 },
      { key: 'albireo', x: 66, y: 63, mag: 3.1 },
    ],
    links: [
      ['deneb', 'sadr'],
      ['sadr', 'albireo'],
      ['sadr', 'gienah'],
      ['sadr', 'delta'],
    ],
    label: { x: 68, y: 36 },
  },
  {
    id: 'lyra',
    name: 'Lyre',
    stars: [
      { key: 'vega', x: 78, y: 28, mag: 0.0 },
      { key: 'sheliak', x: 82, y: 33, mag: 3.5 },
      { key: 'sulafat', x: 86, y: 31, mag: 3.2 },
      { key: 'delta', x: 84, y: 26, mag: 4.2 },
      { key: 'zeta', x: 88, y: 27, mag: 4.3 },
    ],
    links: [
      ['vega', 'delta'],
      ['delta', 'zeta'],
      ['zeta', 'sulafat'],
      ['sulafat', 'sheliak'],
      ['sheliak', 'delta'],
    ],
    label: { x: 80, y: 21 },
  },
  {
    id: 'andromeda',
    name: 'Andromede',
    stars: [
      { key: 'alpheratz', x: 50, y: 22, mag: 2.1 },
      { key: 'mirach', x: 44, y: 28, mag: 2.1 },
      { key: 'almach', x: 38, y: 33, mag: 2.1 },
      { key: 'mu', x: 35, y: 26, mag: 3.9 },
      { key: 'nu', x: 31, y: 29, mag: 4.5 },
    ],
    links: [
      ['alpheratz', 'mirach'],
      ['mirach', 'almach'],
      ['mirach', 'mu'],
      ['mu', 'nu'],
    ],
    label: { x: 37, y: 19 },
  },
  {
    id: 'pegasus',
    name: 'Pegase',
    stars: [
      { key: 'markab', x: 56, y: 30, mag: 2.5 },
      { key: 'scheat', x: 63, y: 29, mag: 2.4 },
      { key: 'algenib', x: 58, y: 37, mag: 2.8 },
      { key: 'alpheratz', x: 50, y: 22, mag: 2.1 },
    ],
    links: [
      ['markab', 'scheat'],
      ['scheat', 'alpheratz'],
      ['alpheratz', 'algenib'],
      ['algenib', 'markab'],
    ],
    label: { x: 57, y: 41 },
  },
  {
    id: 'scorpius',
    name: 'Scorpion',
    stars: [
      { key: 'antares', x: 74, y: 72, mag: 1.1 },
      { key: 'acrux', x: 78, y: 78, mag: 2.6 },
      { key: 'dschubba', x: 70, y: 67, mag: 2.3 },
      { key: 'shaula', x: 82, y: 84, mag: 1.6 },
      { key: 'sargas', x: 86, y: 78, mag: 1.9 },
      { key: 'jabbah', x: 67, y: 62, mag: 2.7 },
    ],
    links: [
      ['jabbah', 'dschubba'],
      ['dschubba', 'antares'],
      ['antares', 'acrux'],
      ['acrux', 'shaula'],
      ['shaula', 'sargas'],
    ],
    label: { x: 76, y: 66 },
  },
];

const isLikelyTestDonor = (supporter) => {
  const text = `${supporter?.display_name || ''} ${supporter?.name || ''} ${supporter?.email || ''}`.toLowerCase();
  return /(test|pytest|webhook|log\s*check|@example\.com)/i.test(text);
};

const buildBackgroundStars = (count = 460) => {
  let seed = 2701;
  const rand = () => {
    seed = (seed * 48271) % 2147483647;
    return seed / 2147483647;
  };

  return Array.from({ length: count }).map((_, index) => ({
    id: `bg-star-${index}`,
    x: 1 + rand() * 98,
    y: 1 + rand() * 98,
    r: 0.03 + rand() * 0.12,
    opacity: 0.15 + rand() * 0.6,
    delay: rand() * 3,
    duration: 1.5 + rand() * 2.6,
  }));
};

const buildConstellationGraph = () => {
  const nodes = [];
  const links = [];

  CONSTELLATIONS.forEach((constellation) => {
    const map = {};
    constellation.stars.forEach((star) => {
      const node = {
        id: `${constellation.id}-${star.key}`,
        x: star.x,
        y: star.y,
        size: Math.max(0.35, 3.2 - star.mag) * 0.22,
        constellation: constellation.name,
      };
      map[star.key] = node;
      nodes.push(node);
    });

    constellation.links.forEach(([from, to]) => {
      if (map[from] && map[to]) links.push([map[from], map[to]]);
    });
  });

  return { nodes, links };
};

const buildDonorNodes = (supporters, constellationNodes) => {
  const anchors = constellationNodes.length > 0 ? constellationNodes : [{ id: 'fallback-anchor', x: 50, y: 50, constellation: 'Ciel' }];

  return supporters
    .filter((supporter) => !isLikelyTestDonor(supporter))
    .map((supporter, index) => {
      const name = supporter.display_name || supporter.name || 'Donateur';
      const stars = Number(supporter.stars || 1);
      const anchor = anchors[index % anchors.length];

      return {
        id: `donor-${supporter.id || index}`,
        starNumber: index + 1,
        x: anchor.x,
        y: anchor.y,
        donorName: name,
        stars,
        size: 0.35 + Math.min(stars, 12) * 0.045,
        createdAt: supporter.created_at || '',
        constellation: anchor.constellation,
      };
    });
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const CarteEtoiles = () => {
  const [supporters, setSupporters] = useState(FALLBACK_SUPPORTERS);
  const [search, setSearch] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(1.1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const surfaceRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });

  useEffect(() => {
    const fetchSupporters = async () => {
      try {
        const res = await axios.get(`${API}/supporters/stars?limit=500`);
        if (Array.isArray(res.data) && res.data.length > 0) {
          setSupporters(res.data);
        }
      } catch (error) {
        console.error('Unable to fetch supporters:', error);
      }
    };
    fetchSupporters();
  }, []);

  const backgroundStars = useMemo(() => buildBackgroundStars(), []);
  const constellationGraph = useMemo(() => buildConstellationGraph(), []);
  const donorNodes = useMemo(() => buildDonorNodes(supporters, constellationGraph.nodes), [supporters, constellationGraph.nodes]);

  const filteredDonors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return donorNodes;
    const qNumber = Number.parseInt(q, 10);
    return donorNodes.filter((node) =>
      node.donorName.toLowerCase().includes(q) ||
      node.constellation.toLowerCase().includes(q) ||
      (!Number.isNaN(qNumber) && node.starNumber === qNumber)
    );
  }, [donorNodes, search]);

  const highlightedIds = useMemo(() => new Set(filteredDonors.map((d) => d.id)), [filteredDonors]);

  const focusNode = (node) => {
    setSelectedId(node.id);
    setZoom(1.7);
    setPan({
      x: clamp(50 - node.x, -48, 48),
      y: clamp(50 - node.y, -48, 48),
    });
  };

  const onMouseDown = (event) => {
    if (event.button !== 0) return;
    setIsDragging(true);
    dragRef.current = {
      active: true,
      startX: event.clientX,
      startY: event.clientY,
      originX: pan.x,
      originY: pan.y,
    };
  };

  const onMouseMove = (event) => {
    if (!dragRef.current.active) return;
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect) return;

    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;
    const worldDx = (deltaX / rect.width) * (100 / zoom);
    const worldDy = (deltaY / rect.height) * (100 / zoom);

    setPan({
      x: clamp(dragRef.current.originX + worldDx, -52, 52),
      y: clamp(dragRef.current.originY + worldDy, -52, 52),
    });
  };

  const onMouseUp = () => {
    dragRef.current.active = false;
    setIsDragging(false);
  };

  const onWheel = (event) => {
    event.preventDefault();
    const next = clamp(zoom + (event.deltaY < 0 ? 0.08 : -0.08), 0.9, 2.6);
    setZoom(next);
  };

  const selectedNode = donorNodes.find((node) => node.id === selectedId) || null;

  return (
    <main className="min-h-screen bg-obsidian">
      <section className="relative min-h-screen overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(77,183,255,0.22),transparent_36%),radial-gradient(circle_at_74%_12%,rgba(159,124,255,0.2),transparent_32%),radial-gradient(circle_at_50%_84%,rgba(255,215,120,0.18),transparent_42%),radial-gradient(circle_at_50%_50%,rgba(7,10,20,0.96),rgba(2,3,8,1))]" />

        <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[1400px] flex-col px-4 pb-6 pt-24 md:px-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-gold">
                <ArrowLeft className="h-4 w-4" /> Retour a l'accueil
              </Link>
              <h1 className="mt-2 font-syne text-3xl font-bold text-white md:text-4xl">Carte des etoiles des donateurs</h1>
              <p className="mt-1 text-sm text-white/60">
                Recherchez un nom ou un numero d'etoile, survolez une etoile pour voir le donateur, puis explorez librement le ciel.
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-white/15 bg-obsidian/70 px-3 py-2">
              <Search className="h-4 w-4 text-white/50" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un nom ou numero..."
                className="w-56 bg-transparent text-sm text-white outline-none placeholder:text-white/40"
              />
            </div>
          </div>

          <div className="grid flex-1 gap-4 lg:grid-cols-[1fr_320px]">
            <div
              ref={surfaceRef}
              className={`relative overflow-hidden rounded-2xl border border-white/10 bg-obsidian/35 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              onWheel={onWheel}
            >
              <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                {backgroundStars.map((star) => (
                  <circle
                    key={star.id}
                    cx={star.x}
                    cy={star.y}
                    r={star.r}
                    fill="rgba(255,255,255,0.95)"
                    opacity={star.opacity}
                    style={{ animation: `pulse ${star.duration}s ease-in-out ${star.delay}s infinite` }}
                  />
                ))}

                <g transform={`translate(50 50) scale(${zoom}) translate(${-50 + pan.x} ${-50 + pan.y})`}>
                  {constellationGraph.links.map(([from, to], idx) => (
                    <line
                      key={`line-${idx}`}
                      x1={from.x}
                      y1={from.y}
                      x2={to.x}
                      y2={to.y}
                      stroke="rgba(136, 210, 255, 0.38)"
                      strokeWidth="0.13"
                      strokeDasharray="0.45 0.35"
                    />
                  ))}

                  {CONSTELLATIONS.map((constellation) => (
                    <text
                      key={constellation.id}
                      x={constellation.label.x}
                      y={constellation.label.y}
                      fill="rgba(172, 223, 255, 0.52)"
                      fontSize="1.1"
                      letterSpacing="0.14"
                    >
                      {constellation.name}
                    </text>
                  ))}

                  {constellationGraph.nodes.map((node, idx) => (
                    <circle
                      key={node.id}
                      cx={node.x}
                      cy={node.y}
                      r={node.size}
                      fill="rgba(222, 242, 255, 0.92)"
                      style={{ animation: `pulse ${2 + (idx % 4) * 0.3}s ease-in-out ${idx * 0.08}s infinite` }}
                    />
                  ))}

                  {donorNodes.map((node, idx) => {
                    const isFiltered = search.trim() ? highlightedIds.has(node.id) : true;
                    const isSelected = selectedId === node.id;
                    const isHovered = hoveredId === node.id;

                    return (
                      <g key={node.id}>
                        <circle
                          cx={node.x}
                          cy={node.y}
                          r={node.size + (isSelected ? 0.28 : 0)}
                          fill={isFiltered ? 'rgba(255,223,133,0.98)' : 'rgba(255,223,133,0.25)'}
                          style={{ animation: `pulse ${1.8 + (idx % 5) * 0.25}s ease-in-out ${idx * 0.06}s infinite` }}
                        />
                        <circle
                          cx={node.x}
                          cy={node.y}
                          r={Math.max(1.25, node.size + 0.8)}
                          fill="transparent"
                          onMouseEnter={() => setHoveredId(node.id)}
                          onMouseLeave={() => setHoveredId(null)}
                          onClick={() => focusNode(node)}
                        />

                        {(isHovered || isSelected) && (
                          <g>
                            <rect
                              x={node.x + 1}
                              y={node.y - 3.1}
                              width={Math.max(12.5, node.donorName.length * 0.85 + 10)}
                              height="3.35"
                              rx="0.8"
                              fill="rgba(10, 12, 22, 0.9)"
                              stroke="rgba(255, 215, 0, 0.45)"
                              strokeWidth="0.13"
                            />
                            <text
                              x={node.x + 1.7}
                              y={node.y - 1.35}
                              fill="rgba(255,255,255,0.96)"
                              fontSize="1.45"
                            >
                                #{node.starNumber} • {node.donorName} • {node.stars} • {node.constellation}
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })}
                </g>
              </svg>

              <div className="absolute left-3 top-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoom((z) => clamp(z + 0.15, 0.85, 2.7))}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/20 bg-obsidian/70 text-white hover:border-gold/60"
                  aria-label="Zoom avant"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setZoom((z) => clamp(z - 0.15, 0.85, 2.7))}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/20 bg-obsidian/70 text-white hover:border-gold/60"
                  aria-label="Zoom arriere"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setZoom(1.1);
                    setPan({ x: 0, y: 0 });
                    setSelectedId(null);
                  }}
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/20 bg-obsidian/70 px-3 text-xs uppercase tracking-wider text-white hover:border-gold/60"
                >
                  <Crosshair className="h-3.5 w-3.5" /> Recentrer
                </button>
              </div>

              <div className="absolute bottom-3 right-3 rounded-lg border border-white/15 bg-obsidian/70 px-3 py-2 text-xs text-white/75">
                Maintenez clic gauche + glisser pour naviguer • Molette pour zoomer • Survolez une etoile pour voir le nom
              </div>
            </div>

            <aside className="rounded-2xl border border-white/10 bg-obsidian/45 p-4">
              <h2 className="font-syne text-lg font-bold text-white">Donateurs</h2>
              <p className="mt-1 text-xs text-white/60">
                {filteredDonors.length} resultat{filteredDonors.length > 1 ? 's' : ''} sur {donorNodes.length}
              </p>

              {selectedNode && (
                <div className="mt-3 rounded-xl border border-gold/35 bg-gold/10 p-3 text-sm text-white">
                  <p className="font-semibold text-gold">Etoile selectionnee</p>
                  <p className="mt-1">#{selectedNode.starNumber} • {selectedNode.donorName}</p>
                  <p className="text-white/70">{selectedNode.stars} etoile{selectedNode.stars > 1 ? 's' : ''} • {selectedNode.constellation}</p>
                </div>
              )}

              <div className="mt-3 max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                {filteredDonors.map((node) => (
                  <button
                    key={`row-${node.id}`}
                    type="button"
                    onClick={() => focusNode(node)}
                    className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${selectedId === node.id ? 'border-gold/55 bg-gold/10' : 'border-white/10 hover:border-gold/35 hover:bg-white/5'}`}
                  >
                    <p className="truncate text-sm font-medium text-white">#{node.starNumber} • {node.donorName}</p>
                    <p className="text-xs text-white/65">{node.stars} etoile{node.stars > 1 ? 's' : ''} • {node.constellation}</p>
                  </button>
                ))}

                {filteredDonors.length === 0 && (
                  <p className="rounded-lg border border-white/10 px-3 py-3 text-sm text-white/70">
                    Aucun donateur ne correspond a cette recherche.
                  </p>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CarteEtoiles;
